import React, { useState, useEffect, useCallback } from 'react';
import { ELEMENTS, DEFAULT_CYCLE_STAGES, EMPTY_ELEMENT_ENTRY, DEFAULT_RATING_EMOJI_SET } from './utils/constants';
import { useAuth } from './contexts/AuthContext';
import { useSupabaseStorage } from './utils/supabaseStorage';
import { ThemeProvider } from './contexts/ThemeContext';
import useDeviceDetection from './hooks/useDeviceDetection';
import OnboardingScreen from './components/OnboardingScreen';
import Home from './components/Home';
import IndividualLogFlow from './components/IndividualLogFlow';
import Insights from './components/Insights';
import Settings from './components/Settings';
import EditUtopia from './components/EditUtopia';
import PreviousDay from './components/PreviousDay';
import BottomNav from './components/BottomNav';
import Login from './components/Login';
import AppNotAccessible from './components/AppNotAccessible';

const createDefaultCycleTracking = () => ({
  enabled: false,
  cycleStartDate: null,
  stages: DEFAULT_CYCLE_STAGES.map((stage) => ({ ...stage })),
});

const createDefaultRatingTracking = () => ({
  enabled: false,
  emojiSet: [...DEFAULT_RATING_EMOJI_SET],
});

const createEmptyElementEntry = () => ({ ...EMPTY_ELEMENT_ENTRY });

const createEmptyDraft = () => {
  const blank = {};
  ELEMENTS.forEach((el) => {
    blank[el.key] = createEmptyElementEntry();
  });
  return blank;
};

const normalizeElementEntry = (value = {}) => ({
  text: value.text || '',
  action: value.action || '',
  redirect: Boolean(value.redirect),
  cycleNote: value.cycleNote || '',
  rating: value.rating || null,
});

const normalizeEntryValues = (values) => {
  const normalized = createEmptyDraft();
  if (values && typeof values === 'object') {
    Object.entries(values).forEach(([key, value]) => {
      if (normalized[key]) {
        normalized[key] = normalizeElementEntry(value);
      }
    });
  }
  return normalized;
};

const normalizeEntries = (entries = []) =>
  entries.map((entry) => ({
    ...entry,
    values: normalizeEntryValues(entry.values),
  }));

const normalizeCycleTracking = (cycleTracking) => {
  const defaults = createDefaultCycleTracking();
  if (!cycleTracking || typeof cycleTracking !== 'object') {
    return defaults;
  }

  const stages = Array.isArray(cycleTracking.stages) && cycleTracking.stages.length
    ? cycleTracking.stages.map((stage, index) => {
        const fallback = defaults.stages[index % defaults.stages.length];
        const lengthNumber = Number(stage?.length);
        return {
          id: stage?.id || fallback.id || `stage-${index}`,
          name: stage?.name || fallback.name,
          length: Number.isFinite(lengthNumber) && lengthNumber > 0 ? Math.round(lengthNumber) : fallback.length,
        };
      })
    : defaults.stages;

  return {
    enabled: Boolean(cycleTracking.enabled),
    cycleStartDate: cycleTracking.cycleStartDate || null,
    stages,
  };
};

const normalizeRatingTracking = (ratingTracking) => {
  const defaults = createDefaultRatingTracking();
  if (!ratingTracking || typeof ratingTracking !== 'object') {
    return defaults;
  }

  const emojiSet = Array.isArray(ratingTracking.emojiSet) && ratingTracking.emojiSet.length > 0
    ? ratingTracking.emojiSet
    : defaults.emojiSet;

  return {
    enabled: Boolean(ratingTracking.enabled),
    emojiSet: [...emojiSet],
  };
};

function App() {
  const {
    user,
    loading,
    isConfigured,
    databaseHealthy,
    isCheckingDatabase,
    testDatabase
  } = useAuth();
  const { loadState, saveState, isSupabaseAvailable } = useSupabaseStorage();
  const { isMobile } = useDeviceDetection();

  const [state, setState] = useState(() => ({
    onboardingDone: false,
    utopia: { earth: "", water: "", air: "", fire: "" },
    theme: "Stoicism",
    entries: [],
    streakGoal: false,
    cycleTracking: createDefaultCycleTracking(),
    ratingTracking: createDefaultRatingTracking(),
  }));
  const [dataLoaded, setDataLoaded] = useState(false);
  const [initialSaveDone, setInitialSaveDone] = useState(false);
  const [isIndividualLogging, setIsIndividualLogging] = useState(false);

  // Load data when user is authenticated
  useEffect(() => {
    if (user && !dataLoaded) {
      loadState().then((loadedState) => {
        if (loadedState) {
          setState((prev) => {
            // Handle cycleTracking and ratingTracking separately to avoid overwriting with null/undefined
            const { cycleTracking: loadedCycleTracking, ratingTracking: loadedRatingTracking, ...restOfLoadedState } = loadedState;
            const normalizedCycleTracking = loadedCycleTracking !== undefined && loadedCycleTracking !== null
              ? normalizeCycleTracking(loadedCycleTracking)
              : prev.cycleTracking;
            const normalizedRatingTracking = loadedRatingTracking !== undefined && loadedRatingTracking !== null
              ? normalizeRatingTracking(loadedRatingTracking)
              : prev.ratingTracking;
            
            return {
              ...prev,
              ...restOfLoadedState,
              utopia: { ...prev.utopia, ...(loadedState.utopia || {}) },
              entries: normalizeEntries(loadedState.entries || []),
              cycleTracking: normalizedCycleTracking,
              ratingTracking: normalizedRatingTracking,
            };
          });

          const today = new Date().toISOString().slice(0, 10);
          const todayEntry = (loadedState.entries || []).find((entry) => entry.date === today);
          if (todayEntry) {
            setTodayDraft(normalizeEntryValues(todayEntry.values));
          }
        }
        setDataLoaded(true);
        // Mark that we've completed the initial load
        setTimeout(() => setInitialSaveDone(true), 2000);
      });
    }
  }, [user, dataLoaded, loadState]);

  // Save state when it changes (with debouncing)
  useEffect(() => {
    if (dataLoaded && initialSaveDone && !isIndividualLogging) {
      saveState(state);
    }
  }, [state, dataLoaded, initialSaveDone, isIndividualLogging, saveState]);

  // Helper function to check if utopia is complete
  const isUtopiaComplete = useCallback((utopia) => {
    return utopia && Object.values(utopia).every(value => value && value.trim() !== "");
  }, []);

  // Initialize route from URL if available
  const getInitialRoute = () => {
    const path = window.location.pathname;
    if (path === "/" || path === "") return "home";
    return path.substring(1); // Remove leading slash
  };

  const [route, setRouteState] = useState(getInitialRoute()); // Initialize from URL
  const [routeManuallySet, setRouteManuallySet] = useState(false);

  // Set the correct route when data is loaded (only run once)
  useEffect(() => {
    if (dataLoaded && !routeManuallySet) {
      if (state.onboardingDone && isUtopiaComplete(state.utopia)) {
        // Only redirect to home if not already on a valid route
        if (route === "onboard-1" || route === "onboard-2" || route === "onboard-3" || route === "onboard-4") {
          setRouteState("home");
          window.history.replaceState({ route: "home" }, "", "/");
        } else {
          // Ensure initial route has proper history state
          const url = route === "home" ? "/" : `/${route}`;
          console.log('Setting initial history state:', { route }, url);
          window.history.replaceState({ route }, "", url);
        }
      } else {
        // Only redirect to onboarding if not already on onboarding
        if (!route.startsWith("onboard")) {
          setRouteState("onboard-1");
          window.history.replaceState({ route: "onboard-1" }, "", "/onboard");
        } else {
          // Ensure initial onboarding route has proper history state
          const url = route === "onboard-1" ? "/onboard" : `/${route}`;
          window.history.replaceState({ route }, "", url);
        }
      }
    }
  }, [dataLoaded, state.onboardingDone, state.utopia, routeManuallySet, isUtopiaComplete, route]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      console.log('PopState event:', event.state);
      if (event.state && event.state.route) {
        setRouteState(event.state.route);
        setRouteManuallySet(true);
      } else {
        // Default to home if no state
        console.log('No state found, defaulting to home');
        setRouteState("home");
        setRouteManuallySet(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const setRoute = (newRoute) => {
    console.log('setRoute called with:', newRoute);
    setRouteState(newRoute);
    setRouteManuallySet(true);

    // Push to browser history for proper back button behavior
    const url = newRoute === "home" ? "/" : `/${newRoute}`;
    console.log('Pushing to history:', { route: newRoute }, url);
    window.history.pushState({ route: newRoute }, "", url);
  };

  // Function to navigate back using browser history
  const navigateBack = () => {
    window.history.back();
  };
  const [tempUtopia, setTempUtopia] = useState(state.utopia);

  const [todayDraft, setTodayDraft] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    const existing = state.entries.find((e) => e.date === today);
    if (existing) return normalizeEntryValues(existing.values);
    return createEmptyDraft();
  });

  useEffect(() => {
    if (isIndividualLog(route)) {
      // Reset draft to only saved data when switching between individual element routes
      const today = new Date().toISOString().slice(0, 10);
      const existing = state.entries.find((e) => e.date === today);
      if (existing) {
        setTodayDraft(normalizeEntryValues(existing.values));
      } else {
        setTodayDraft(createEmptyDraft());
      }
      setIsIndividualLogging(true);
    } else {
      setIsIndividualLogging(false);
    }
  }, [route, state.entries]);

  const completeOnboarding = () => {
    setState((s) => ({ ...s, onboardingDone: true, utopia: tempUtopia }));
    setRouteState("home");
  };

  const updateDraft = (elKey, field, value) => {
    setTodayDraft((d) => ({
      ...d,
      [elKey]: {
        ...normalizeElementEntry(d[elKey]),
        [field]: field === 'redirect' ? Boolean(value) : value,
      },
    }));
  };


  const saveElement = async (elementKey, elementData) => {
    const today = new Date().toISOString().slice(0, 10);
    const existingIdx = state.entries.findIndex((e) => e.date === today);
    const newValues = {
      ...normalizeEntryValues(todayDraft),
      [elementKey]: normalizeElementEntry(elementData),
    };
    const newEntry = { date: today, values: newValues };
    const newEntries = [...state.entries];
    if (existingIdx >= 0) newEntries[existingIdx] = newEntry; else newEntries.unshift(newEntry);
    setState((s) => ({ ...s, entries: newEntries }));
    setTodayDraft(newValues);

    // Save to database immediately when explicitly requested
    try {
      await saveState({ ...state, entries: newEntries });
    } catch (error) {
      console.error('Error saving element to database:', error);
    }
  };

  const isOnboarding = (r) => r.startsWith("onboard");
  const isIndividualLog = (r) => r.startsWith("log-");
  const getElementKeyFromRoute = (r) => r.replace("log-", "");
  const isEditUtopia = (r) => r === "edit-utopia";
  const isPreviousDay = (r) => r === "previous-day";

  // Show AppNotAccessible if database is not healthy
  if (isConfigured && !databaseHealthy && !loading) {
    return (
      <AppNotAccessible
        onRetry={testDatabase}
        isRetrying={isCheckingDatabase}
      />
    );
  }

  // Show loading spinner while auth is loading or data is loading
  if (loading || (user && !dataLoaded)) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
            <p className="mt-2 text-gray-600 dark:text-dark-textSecondary">
              {loading ? 'Loading...' : 'Syncing your data...'}
            </p>
            {isSupabaseAvailable && (
              <p className="mt-1 text-xs text-gray-500 dark:text-dark-muted">Connected to cloud storage</p>
            )}
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // Show login screen if not configured or not authenticated
  if (!isConfigured || !user) {
    return <Login />;
  }

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-dark-text flex flex-col" style={{ minHeight: '100vh' }}>
        <div className={`${isMobile ? 'max-w-md' : 'max-w-4xl'} w-full mx-auto bg-gray-50 dark:bg-dark-bg border-l border-r dark:border-dark-border`}>
          {isOnboarding(route) && (
            <div className="h-screen bg-white dark:bg-dark-surface">
              <div className="p-6">
                <h1 className="text-2xl font-bold dark:text-dark-text">Welcome to Elements</h1>
                <p className="text-sm text-gray-600 dark:text-dark-textSecondary mt-2">Notice. Reflect. Align.</p>
              </div>
              <div className="p-4">
                {route === "onboard-1" && (
                  <OnboardingScreen
                    stepIndex={0}
                    tempUtopia={tempUtopia}
                    setTempUtopia={setTempUtopia}
                    setRoute={setRoute}
                    completeOnboarding={completeOnboarding}
                  />
                )}
                {route === "onboard-2" && (
                  <OnboardingScreen
                    stepIndex={1}
                    tempUtopia={tempUtopia}
                    setTempUtopia={setTempUtopia}
                    setRoute={setRoute}
                    completeOnboarding={completeOnboarding}
                  />
                )}
                {route === "onboard-3" && (
                  <OnboardingScreen
                    stepIndex={2}
                    tempUtopia={tempUtopia}
                    setTempUtopia={setTempUtopia}
                    setRoute={setRoute}
                    completeOnboarding={completeOnboarding}
                  />
                )}
                {route === "onboard-4" && (
                  <OnboardingScreen
                    stepIndex={3}
                    tempUtopia={tempUtopia}
                    setTempUtopia={setTempUtopia}
                    setRoute={setRoute}
                    completeOnboarding={completeOnboarding}
                  />
                )}
              </div>
            </div>
          )}
          {route === "home" && <Home state={state} setRoute={setRoute} />}
          {isIndividualLog(route) && (
            <IndividualLogFlow
              elementKey={getElementKeyFromRoute(route)}
              todayDraft={todayDraft}
              updateDraft={updateDraft}
              state={state}
              setRoute={setRoute}
              saveElement={saveElement}
              navigateBack={navigateBack}
            />
          )}
          {route === "insights" && <Insights state={state} setRoute={setRoute} />}
          {route === "settings" && <Settings state={state} setState={setState} setRoute={setRoute} />}
          {isEditUtopia(route) && <EditUtopia state={state} setState={setState} setRoute={setRoute} />}
          {isPreviousDay(route) && <PreviousDay state={state} setState={setState} setRoute={setRoute} />}
          {!isOnboarding(route) && !isIndividualLog(route) && !isEditUtopia(route) && !isPreviousDay(route) && <BottomNav route={route} setRoute={setRoute} />}
        </div>
      </div>
    </ThemeProvider>
  );
}

export default App;
